/**
 * In-app message filtering and dismissal (`GET /messages`, docs/plan/api.md; docs/plan/notifications.md "In-app"):
 * the server already answers only live messages, but a cached copy can outlive its own `endsAt` or still be
 * waiting for its `startsAt`, so the client re-checks both. Dismissals live in localStorage, same as the phone.
 */
import type { InAppMessage, MessageScreen } from '../../api/types'
import { readCache, writeCache } from '../../lib/offlineCache'

/** Where each fixed screen key (docs/plan/notifications.md) lives on the web; an unknown key opens Today. */
const SCREEN_PATHS: Record<MessageScreen, string> = {
  today: '/today',
  log: '/diary',
  train: '/train',
  progress: '/progress',
  settings: '/settings',
  account: '/settings/data',
}

export function screenPath(screen: string): string {
  return SCREEN_PATHS[screen as MessageScreen] ?? SCREEN_PATHS.today
}

/**
 * Extra pages a screen's messages also apply to, beyond its own path and anything nested under it. Train and
 * Progress are hubs now (redesign.md §3): their workouts/programs/equipment and weight/expenditure/nutrients/
 * photos/history pages keep their own existing URLs rather than nesting under `/train` or `/progress`, so a message
 * scoped to one of those screens needs the full list here too.
 */
const SCREEN_EXTRA_PATHS: Partial<Record<MessageScreen, readonly string[]>> = {
  train: ['/workouts', '/programs', '/gyms'],
  progress: ['/weight', '/expenditure', '/nutrients', '/photos', '/history'],
}

/** "any" shows everywhere; any other screen only on its own page (and pages nested under it, or listed above). */
export function messageAppliesToPath(message: InAppMessage, pathname: string): boolean {
  if (message.inapp.screen === 'any') return true
  const screen = message.inapp.screen
  const paths = [screenPath(screen), ...(SCREEN_EXTRA_PATHS[screen as MessageScreen] ?? [])]
  return paths.some((path) => pathname === path || pathname.startsWith(`${path}/`))
}

/** ISO instants compare lexically here because every date the server sends is UTC `...Z`. */
export function messageIsLive(message: InAppMessage, nowIso: string): boolean {
  if (message.startsAt && nowIso < message.startsAt) return false
  if (message.endsAt && nowIso > message.endsAt) return false
  return true
}

/** The live, not-yet-dismissed messages for [pathname], in the order the server sent them. */
export function visibleMessages(messages: readonly InAppMessage[], pathname: string, dismissed: ReadonlySet<string>, now: Date = new Date()): InAppMessage[] {
  const nowIso = now.toISOString()
  return messages.filter((message) => messageIsLive(message, nowIso) && messageAppliesToPath(message, pathname) && !dismissed.has(message.id))
}

const MESSAGES_CACHE_KEY = 'macrotide.messages.cache'
const DISMISSED_KEY = 'macrotide.messages.dismissed'

export function readCachedMessages(): InAppMessage[] | null {
  return readCache<InAppMessage[]>(MESSAGES_CACHE_KEY)
}

export function writeCachedMessages(messages: readonly InAppMessage[]): void {
  writeCache(MESSAGES_CACHE_KEY, messages)
}

export function readDismissed(): Set<string> {
  return new Set(readCache<string[]>(DISMISSED_KEY) ?? [])
}

export function writeDismissed(ids: ReadonlySet<string>): void {
  writeCache(DISMISSED_KEY, [...ids])
}
